/**
 * How the SDK presents a result against what Playwright expected of it.
 *
 * Playwright counts a result as expected only when
 * `result.status === test.expectedStatus` (teleReceiver's
 * computeTestCaseOutcome), so a `test.fail()` that failed is a pass, and one
 * that passed is a failure that its list reporter prints as "Expected to fail,
 * but passed.". The SDK's data files keep `status` as the actual result and
 * carry `expectedStatus` beside it (the #135 contract); this module is where
 * the two are turned into what a person reads, in the terminal box and in the
 * HTML report alike.
 *
 * The HTML report runs in the browser as ES5 inside a generated string, so it
 * cannot import this module. It receives the two tables below as JSON instead
 * (html-js-render.ts), which keeps the terminal and the report from ever
 * disagreeing about a pair: there is one table, not two copies of a rule.
 */

import type { Summary, TestStatus } from '@testrelic/core';

/**
 * Keyed `${expected}|${actual}`, in the SDK vocabulary (mapPlaywrightStatus).
 * Only pairs whose presentation differs from the actual status are listed.
 *
 * Deliberately absent:
 * - `failed|timedout`: a `test.fail()` that timed out is a failure to
 *   Playwright too (only an exact match is expected), and 'timedout' already
 *   reads as one.
 * - `failed|flaky` and `*|skipped`: those statuses come from Playwright's own
 *   outcome, which has already applied the expectation.
 */
export const VERDICT_OVERRIDES: Readonly<Record<string, TestStatus>> = {
  'failed|failed': 'passed',
  'failed|passed': 'failed',
};

/** What the report and the terminal say about a pair that was overridden. */
export const EXPECTATION_NOTES: Readonly<Record<string, string>> = {
  'failed|failed': 'Failed as expected',
  // Playwright's own words (reporters/base.js), so the two reports agree.
  'failed|passed': 'Expected to fail, but passed.',
};

/** An absent expectation means 'passed', as on the index row. */
function pairKey(status: TestStatus, expectedStatus?: TestStatus | null): string {
  return `${expectedStatus ?? 'passed'}|${status}`;
}

/** The status a person should see: the actual one unless the pair overrides it. */
export function presentedStatus(status: TestStatus, expectedStatus?: TestStatus | null): TestStatus {
  return VERDICT_OVERRIDES[pairKey(status, expectedStatus)] ?? status;
}

/** 'Failed as expected' / 'Expected to fail, but passed.', or null for any other pair. */
export function expectationNote(status: TestStatus, expectedStatus?: TestStatus | null): string | null {
  return EXPECTATION_NOTES[pairKey(status, expectedStatus)] ?? null;
}

/**
 * The run's counts as a person should read them, next to how many of them the
 * expectation moved. Rendered only: summary.json, report.json and the cloud
 * payload keep counting by actual status.
 */
export interface PresentedCounts {
  readonly total: number;
  readonly passed: number;
  readonly failed: number;
  readonly flaky: number;
  readonly skipped: number;
  readonly timedout: number;
  /** `test.fail()` tests that failed: counted under `passed` here. */
  readonly expectedFailures: number;
  /** `test.fail()` tests that passed: counted under `failed` here. */
  readonly unexpectedPasses: number;
}

type CountKey = 'passed' | 'failed' | 'flaky' | 'skipped' | 'timedout';

/**
 * Start from the summary's own counts and move only the final attempts whose
 * presented status differs from the actual one. A run without `test.fail()`
 * therefore gets exactly the summary back, and `total` never changes.
 *
 * `finals` must be the same final attempts the summary was counted from, one
 * per test; retries are not the caller's to pass.
 */
export function presentCounts(
  summary: Pick<Summary, 'total' | 'passed' | 'failed' | 'flaky' | 'skipped' | 'timedout'>,
  finals: Iterable<{ readonly status: TestStatus; readonly expectedStatus?: TestStatus | null }>,
): PresentedCounts {
  const c: Record<CountKey, number> = {
    passed: summary.passed,
    failed: summary.failed,
    flaky: summary.flaky,
    skipped: summary.skipped,
    timedout: summary.timedout,
  };
  let expectedFailures = 0;
  let unexpectedPasses = 0;
  for (const t of finals) {
    const shown = presentedStatus(t.status, t.expectedStatus);
    if (shown === t.status) continue;
    c[t.status as CountKey]--;
    c[shown as CountKey]++;
    if (shown === 'passed') expectedFailures++;
    else unexpectedPasses++;
  }
  return { total: summary.total, ...c, expectedFailures, unexpectedPasses };
}
